import {
  ArrowRight,
  Box,
  Check,
  ChevronDown,
  Clock,
  FileCheck2,
  FileText,
  FlaskConical,
  Globe,
  HelpCircle,
  Info,
  Lock,
  Minus,
  PackageCheck,
  Plus,
  Route,
  ScanLine,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Truck,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import mark from './assets/brand/mark.svg';
import { AMINO_OFFERS, BULK_OFFER, getOfferSummary, money, type OfferSelection } from './data/offers';
import { processSteps, product, trustPillars } from './data/product';
import { tagadaCheckoutConfigured, useTagadaCheckoutSession } from './tagada/checkout';

type NavRoute = 'home' | 'pdp' | 'catalog' | 'coa';

const navItems: { label: string; route: NavRoute }[] = [
  { label: 'Home', route: 'home' },
  { label: 'RT-10', route: 'pdp' },
  { label: 'Catalog', route: 'catalog' },
  { label: 'COA', route: 'coa' }
];

const iconMap = [FileCheck2, FlaskConical, Globe, Lock];

function getRouteFromPath(): NavRoute {
  const path = window.location.pathname;

  if (path === '/' || path.startsWith('/home')) return 'home';
  if (path.startsWith('/catalog')) return 'catalog';
  if (path.startsWith('/coa')) return 'coa';
  return 'pdp';
}

function pathFor(route: NavRoute) {
  if (route === 'home') return '/home';
  if (route === 'catalog') return '/catalog';
  if (route === 'coa') return '/coa/rt-10';
  return '/products/rt-10';
}

function App() {
  const [route, setRoute] = useState<NavRoute>(getRouteFromPath);
  const [selected, setSelected] = useState<OfferSelection>(2);
  const [bulkQty, setBulkQty] = useState(4);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => {
    const onPop = () => setRoute(getRouteFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function navigate(next: NavRoute) {
    window.history.pushState({}, '', pathFor(next));
    setRoute(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const sharedProps = {
    selected,
    setSelected,
    bulkQty,
    setBulkQty,
    onCheckout: () => setCheckoutOpen(true),
    navigate
  };

  return (
    <>
      <PromoBar />
      <Header route={route} navigate={navigate} />
      {route === 'home' && <HomePage navigate={navigate} onCheckout={() => setCheckoutOpen(true)} />}
      {route === 'pdp' && <PdpPage {...sharedProps} />}
      {route === 'catalog' && <CatalogPage navigate={navigate} />}
      {route === 'coa' && <CoaPage />}
      <TrustTicker />
      <FounderNote />
      <Footer navigate={navigate} />
      <StickyCta selected={selected} bulkQty={bulkQty} onCheckout={() => setCheckoutOpen(true)} />
      <CheckoutDrawer
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        selected={selected}
        bulkQty={bulkQty}
      />
    </>
  );
}

function PromoBar() {
  return (
    <div className="promo">
      <PackageCheck aria-hidden="true" size={15} />
      <span>
        Launch allocation - documentation kit included. <strong>Shipping waived on 2+ vials where route is approved.</strong>
      </span>
    </div>
  );
}

function Header({
  route,
  navigate
}: {
  route: NavRoute;
  navigate: (route: NavRoute) => void;
}) {
  return (
    <header className="site-header">
      <div className="container header-inner">
        <button className="brand-link" onClick={() => navigate('home')} aria-label="Aminoscale home">
          <img src={mark} alt="" width="24" height="24" />
          <span>Aminoscale</span>
        </button>
        <nav className="nav" aria-label="Primary navigation">
          {navItems.map((item) => (
            <button
              key={item.route}
              className={route === item.route ? 'nav-link active' : 'nav-link'}
              onClick={() => navigate(item.route)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <button className="header-cta" onClick={() => navigate('pdp')}>
          Shop RT-10
        </button>
      </div>
    </header>
  );
}

function HomePage({
  navigate,
  onCheckout
}: {
  navigate: (route: NavRoute) => void;
  onCheckout: () => void;
}) {
  return (
    <main>
      <section className="home-hero bg-grid">
        <div className="container home-hero-grid">
          <div className="home-copy">
            <Kicker>Research supply - documentation first</Kicker>
            <h1>Documented research supply, built with instrument-grade clarity.</h1>
            <p>
              Lot-tracked, third-party-documented laboratory research compounds. Every allocation ships with a
              documentation kit and a published delivery window.
            </p>
            <div className="button-row">
              <PrimaryButton onClick={() => navigate('pdp')}>
                Shop RT-10 <ArrowRight size={17} />
              </PrimaryButton>
              <SecondaryButton onClick={() => navigate('catalog')}>View catalog</SecondaryButton>
            </div>
            <ProofBar />
          </div>
          <ProductVisual variant="home" />
        </div>
      </section>

      <section className="section">
        <div className="container pillar-grid">
          {trustPillars.map((pillar, index) => {
            const Icon = iconMap[index] ?? ShieldCheck;
            return (
              <article className="pillar-card" key={pillar.title}>
                <span className="icon-box">
                  <Icon size={18} />
                </span>
                <h3>{pillar.title}</h3>
                <p>{pillar.body}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="section pt-small">
        <div className="container featured-band">
          <ProductVisual variant="card" />
          <div className="featured-copy">
            <div className="inline-kicker">
              <Kicker>Featured - RT-10</Kicker>
              <Badge tone="green">COA available</Badge>
            </div>
            <h2>RT-10 Research Compound</h2>
            <p>10 mg sealed research vial. For laboratory research use only.</p>
            <MiniLadder />
            <div className="button-row">
              <PrimaryButton onClick={() => navigate('pdp')}>
                View product <ArrowRight size={17} />
              </PrimaryButton>
              <SecondaryButton onClick={onCheckout}>Checkout preview</SecondaryButton>
            </div>
          </div>
        </div>
      </section>

      <DocumentationProcess />

      <section className="section">
        <div className="container dark-band">
          <div>
            <Kicker>Catalog</Kicker>
            <h2>Built for a growing, documented catalog.</h2>
            <p>
              One approved compound today. Additional SKUs appear only as they clear documentation,
              fulfillment, and support review.
            </p>
          </div>
          <PrimaryButton onClick={() => navigate('catalog')}>
            Browse catalog <ArrowRight size={17} />
          </PrimaryButton>
        </div>
      </section>
    </main>
  );
}

function PdpPage({
  selected,
  setSelected,
  bulkQty,
  setBulkQty,
  onCheckout,
  navigate
}: {
  selected: OfferSelection;
  setSelected: (selection: OfferSelection) => void;
  bulkQty: number;
  setBulkQty: (qty: number) => void;
  onCheckout: () => void;
  navigate: (route: NavRoute) => void;
}) {
  return (
    <main>
      <section className="pdp-hero bg-grid">
        <div className="container pdp-grid">
          <ProductGallery />
          <PurchaseRail
            selected={selected}
            setSelected={setSelected}
            bulkQty={bulkQty}
            setBulkQty={setBulkQty}
            onCheckout={onCheckout}
          />
        </div>
      </section>
      <TrustSections onCheckout={onCheckout} navigate={navigate} />
    </main>
  );
}

function ProductGallery() {
  return (
    <div className="gallery">
      <ProductVisual variant="hero" />
      <div className="gallery-thumbs">
        {[
          ['Label and lot', ScanLine],
          ['COA summary', FileText],
          ['Carton insert', Box]
        ].map(([label, Icon]) => (
          <div className="thumb" key={String(label)}>
            <Icon size={18} />
            <span>{String(label)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductVisual({ variant }: { variant: 'home' | 'card' | 'hero' }) {
  return (
    <div className={`product-visual ${variant}`}>
      <img src={product.image} alt="RT-10 sealed research vial with documentation kit" />
      <span className="fig-label">FIG. 01 - 10MG SEALED RESEARCH VIAL</span>
      <span className="lot-chip">LOT {product.lot}</span>
    </div>
  );
}

function PurchaseRail({
  selected,
  setSelected,
  bulkQty,
  setBulkQty,
  onCheckout
}: {
  selected: OfferSelection;
  setSelected: (selection: OfferSelection) => void;
  bulkQty: number;
  setBulkQty: (qty: number) => void;
  onCheckout: () => void;
}) {
  const summary = getOfferSummary(selected, bulkQty);

  return (
    <aside className="purchase-rail">
      <div>
        <div className="badge-row">
          <Kicker>RT-10</Kicker>
          <Badge tone="green">LOT {product.lot}</Badge>
          <Badge tone="blue">In stock</Badge>
        </div>
        <h1>{product.title}</h1>
        <p>{product.shortDescription}</p>
      </div>

      <ProofBar />

      <div className="offer-block">
        <div className="offer-topline">
          <strong>Select your allocation</strong>
          <span>1 compound - buy up to 12</span>
        </div>
        <div className="offer-list">
          {([1, 2, 3] as const).map((qty) => {
            const offer = AMINO_OFFERS[qty];
            return (
              <OfferCard
                key={offer.label}
                selected={selected === qty}
                onSelect={() => setSelected(qty)}
                title={offer.label}
                total={offer.total}
                compareAt={offer.compareAt}
                perVial={offer.perVial}
                note={offer.note}
                ribbon={offer.ribbon}
              />
            );
          })}
          <OfferCard
            selected={selected === 4}
            onSelect={() => setSelected(4)}
            title={BULK_OFFER.label}
            total={bulkQty * BULK_OFFER.perVial}
            compareAt={bulkQty * 129}
            perVial={BULK_OFFER.perVial}
            note={BULK_OFFER.note}
            ribbon="Buy up to 12"
          >
            <QuantityStepper value={bulkQty} onChange={setBulkQty} />
          </OfferCard>
        </div>
      </div>

      <DocumentationKit />

      <div className="fulfillment-note">
        <Truck size={17} />
        <span>
          Tracked global fulfillment. Most Tier-1 delivery: <strong>9-11 business days</strong> after dispatch.
        </span>
      </div>

      <div className="summary-box">
        <div className="price-line">
          <div>
            <span className="total-price">{money(summary.total)}</span>
            {summary.savings > 0 && <span className="save-chip">You save {money(summary.savings)}</span>}
          </div>
          <span className="qty-label">
            {summary.quantity} vial{summary.quantity > 1 ? 's' : ''} selected
          </span>
        </div>
        <PrimaryButton full onClick={onCheckout}>
          Continue to checkout preview <ArrowRight size={17} />
        </PrimaryButton>
        <p className="review-line">
          <ShieldAlert size={14} /> Checkout is in review mode while payment approval is finalized.
        </p>
      </div>

      <div className="reassurance-row">
        {[
          [FileText, 'Lot docs'],
          [FlaskConical, 'COA'],
          [Route, 'Tracked'],
          [ShieldCheck, 'Support']
        ].map(([Icon, label]) => (
          <span key={String(label)}>
            <Icon size={18} /> {String(label)}
          </span>
        ))}
      </div>

      <p className="ruo-line">
        For laboratory research use only. Not for human or veterinary use. We do not provide medical advice,
        protocol guidance, or human-use instructions.
      </p>
    </aside>
  );
}

function OfferCard({
  selected,
  onSelect,
  title,
  total,
  compareAt,
  perVial,
  note,
  ribbon,
  children
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  total: number;
  compareAt?: number;
  perVial: number;
  note: string;
  ribbon?: string;
  children?: React.ReactNode;
}) {
  const savings = compareAt ? compareAt - total : 0;

  return (
    <div
      className={selected ? 'offer-card selected' : 'offer-card'}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <span className="radio-dot" aria-hidden="true" />
      <span className="offer-main">
        <span className="offer-head">
          <span className="offer-title">{title}</span>
          {ribbon && <Badge tone={ribbon === 'Best value' ? 'blue' : 'green'}>{ribbon}</Badge>}
        </span>
        <span className="offer-meta">
          <span>{money(perVial)} / vial</span>
          {savings > 0 && <strong>Save {money(savings)}</strong>}
        </span>
        <span className="offer-note">{note}</span>
        {children && <span className="offer-children">{children}</span>}
      </span>
      <span className="offer-price">
        {compareAt && <s>{money(compareAt)}</s>}
        <strong>{money(total)}</strong>
      </span>
    </div>
  );
}

function QuantityStepper({ value, onChange }: { value: number; onChange: (qty: number) => void }) {
  return (
    <span className="stepper" onClick={(event) => event.stopPropagation()}>
      <button disabled={value <= BULK_OFFER.min} onClick={() => onChange(Math.max(BULK_OFFER.min, value - 1))}>
        <Minus size={15} />
      </button>
      <span>{value} vials</span>
      <button disabled={value >= BULK_OFFER.max} onClick={() => onChange(Math.min(BULK_OFFER.max, value + 1))}>
        <Plus size={15} />
      </button>
    </span>
  );
}

function DocumentationKit() {
  return (
    <div className="doc-kit">
      <div className="doc-kit-head">
        <PackageCheck size={16} />
        <strong>Documentation kit included - every allocation</strong>
      </div>
      <div className="doc-kit-grid">
        {['Lot-matched COA summary card', 'Lot trace card', 'Handling and storage card', 'Research-use policy insert'].map(
          (item) => (
            <span key={item}>
              <Check size={14} /> {item}
            </span>
          )
        )}
      </div>
    </div>
  );
}

function TrustSections({
  onCheckout,
  navigate
}: {
  onCheckout: () => void;
  navigate: (route: NavRoute) => void;
}) {
  return (
    <>
      <section className="section">
        <div className="container split-section">
          <div>
            <SectionHead mark="01 - Specification" title="Product specs" />
            <SpecTable rows={product.specs} />
          </div>
          <div>
            <SectionHead mark="02 - Assurance" title="What you can verify" />
            <div className="verify-list">
              {product.verification.map((item, index) => {
                const icons = [ScanLine, ShieldCheck, Clock, Route, HelpCircle];
                const Icon = icons[index] ?? Check;
                return (
                  <div className="verify-row" key={item}>
                    <span className="icon-box small">
                      <Icon size={16} />
                    </span>
                    <span>{item}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="coa-band">
        <div className="container coa-grid">
          <div>
            <Kicker>03 - Documentation vault</Kicker>
            <h2>Lot-specific third-party testing</h2>
            <p>
              Every allocation ships with its documentation kit: COA summary, lot trace, handling card, and
              research-use insert. First-batch results are posted to the COA hub.
            </p>
            <div className="button-row">
              <SecondaryDarkButton onClick={() => navigate('coa')}>
                View COA - RT-10
              </SecondaryDarkButton>
              <SecondaryDarkButton>SDS / handling</SecondaryDarkButton>
            </div>
          </div>
          <div className="batch-card">
            <div className="batch-head">
              <span>Batch documentation</span>
              <Badge tone="amber">1 of 2 pending</Badge>
            </div>
            {[
              ['Identity', 'Declared', 'Verified', true],
              ['Purity', 'Declared', 'Verified', true],
              ['Fill / quantity', '10 mg', '10 mg', true],
              ['Appearance', 'Lyophilized', 'Lyophilized', true],
              ['Seal / label', 'Intact', 'Intact', true],
              ['Endotoxin', 'Declared', 'Test pending', false]
            ].map((row) => (
              <div className="batch-row" key={String(row[0])}>
                <span>{String(row[0])}</span>
                <strong>{String(row[1])}</strong>
                <strong className={row[3] ? 'ok' : 'pending'}>{String(row[2])}</strong>
                <i className={row[3] ? 'dot ok' : 'dot pending'} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-band">
        <div className="container cta-inner">
          <div>
            <h3>Documented from lot to doorstep.</h3>
            <p>2 vials is most popular - shipping waived, documentation kit included.</p>
          </div>
          <PrimaryButton onClick={onCheckout}>
            Choose your allocation <ArrowRight size={17} />
          </PrimaryButton>
        </div>
      </section>

      <section className="section">
        <div className="container split-section">
          <div>
            <SectionHead mark="04 - Details" title="Specs, testing and policy" />
            <Accordion
              items={[
                ['Product specs', 'SKU, format, fill size, storage and handling, lot ID, COA and SDS status, and sellable unit.'],
                ['Third-party testing', 'Lot-specific independent testing. Results are posted to the COA hub.'],
                ['Handling and storage', 'Stored and shipped according to product handling requirements.'],
                [
                  "What's included",
                  'One sealed research vial per unit, plus the documentation kit: COA summary, lot trace, handling card, and research-use insert.'
                ],
                [
                  'Research-use policy',
                  'For laboratory research use only. Not for human or veterinary use. We do not provide medical advice, protocol guidance, or human-use instructions.'
                ]
              ]}
            />
          </div>
          <div>
            <SectionHead mark="05 - Fulfillment" title="Tracked global delivery" />
            <SpecTable
              rows={[
                ['Handling', '1-3 business days'],
                ['Tier-1 range', '9-11 business days'],
                ['United States', '7-18 working days'],
                ['United Kingdom', '7-12 working days'],
                ['Germany', '8-15 working days'],
                ['France', '8-15 working days']
              ]}
            />
            <p className="amber-note">
              <Info size={16} /> Tracking may pause during export/import handoff. Windows exclude weekends,
              holidays, and customs review.
            </p>
          </div>
        </div>
      </section>

      <section className="section comparison-section">
        <div className="container">
          <SectionHead
            mark="06 - Comparison"
            title="The cost of buying blind"
            sub="What you give up ordering from an anonymous supplier versus a documented allocation."
          />
          <div className="comparison-grid">
            <ComparisonCard
              title="Anonymous supplier"
              negative
              items={[
                'No independent COA',
                'Unclear lot matching',
                'No published delivery window',
                'No support threshold',
                'Unclear shipping origin'
              ]}
            />
            <ComparisonCard
              title="Aminoscale - documented"
              items={[
                'Lot-specific third-party COA',
                'COA and SDS access included',
                'Published country delivery windows',
                'Support review threshold',
                'Disclosed fulfillment route'
              ]}
            />
          </div>
        </div>
      </section>

      <section className="section faq-section">
        <div className="container narrow">
          <SectionHead mark="07 - FAQ" title="Questions, answered plainly" />
          <Accordion items={product.faqs} />
        </div>
      </section>
    </>
  );
}

function CatalogPage({ navigate }: { navigate: (route: NavRoute) => void }) {
  return (
    <main className="section catalog-page">
      <div className="container">
        <div className="page-head">
          <Kicker>Catalog</Kicker>
          <h1>Research compounds with documentation first.</h1>
          <p>One approved compound today. We list what we can document and fulfill - no fake category depth.</p>
        </div>
        <div className="filter-row">
          <Badge tone="green">All</Badge>
          {['Format', 'Size', 'Documentation', 'Availability', 'Route', 'Destination'].map((filter) => (
            <span className="disabled-filter" key={filter}>
              {filter} <em>soon</em>
            </span>
          ))}
        </div>
        <div className="catalog-grid">
          <article className="catalog-card">
            <div className="catalog-image">
              <img src={product.image} alt="RT-10 sealed research vial" />
              <Badge tone="green">COA available</Badge>
            </div>
            <div className="catalog-body">
              <div className="catalog-meta">
                <span>RT-10</span>
                <Badge tone="blue">In stock</Badge>
              </div>
              <h2>RT-10 Research Compound</h2>
              <p>10 mg sealed vial - For laboratory research use only</p>
              <div className="mini-specs">
                {[
                  ['Format', 'Vial'],
                  ['Size', '10 mg'],
                  ['Routes', 'Tier-1']
                ].map(([key, value]) => (
                  <span key={key}>
                    <small>{key}</small>
                    <strong>{value}</strong>
                  </span>
                ))}
              </div>
              <div className="catalog-bottom">
                <span>
                  Starting at <strong>$129</strong>
                </span>
                <PrimaryButton onClick={() => navigate('pdp')}>View product</PrimaryButton>
              </div>
            </div>
          </article>
          <div className="coming-soon">Additional SKUs appear here once approved, documented, and fulfillable.</div>
        </div>
      </div>
    </main>
  );
}

function CoaPage() {
  return (
    <main className="section">
      <div className="container coa-page">
        <div className="page-head row-head">
          <div>
            <Kicker>Documentation vault</Kicker>
            <h1>COA and documents - RT-10</h1>
            <p>Lot-specific third-party testing for the first batch. Documents mirror the product page and checkout.</p>
          </div>
          <div className="lot-stack">
            <Badge tone="green">LOT {product.lot}</Badge>
            <Badge tone="amber">1 document pending</Badge>
          </div>
        </div>
        <div className="split-section">
          <div>
            <Kicker>Batch documentation</Kicker>
            <SpecTable
              rows={[
                ['Identity', 'Verified'],
                ['Purity', 'Verified'],
                ['Fill / quantity', '10 mg'],
                ['Appearance', 'Lyophilized'],
                ['Seal / label', 'Intact'],
                ['Endotoxin', 'Pending'],
                ['Dispatch lane', 'Tier-1']
              ]}
            />
          </div>
          <div>
            <Kicker>Documents</Kicker>
            <div className="doc-list">
              {product.documents.map(([name, type, meta, status]) => (
                <div className="doc-row" key={name}>
                  <span className={status === 'Available' ? 'doc-icon ok' : 'doc-icon pending'}>
                    {status === 'Available' ? <FileText size={17} /> : <Clock size={17} />}
                  </span>
                  <span>
                    <strong>{name}</strong>
                    <small>
                      {type} - {meta}
                    </small>
                  </span>
                  {status === 'Available' ? <button>PDF</button> : <em>Pending</em>}
                </div>
              ))}
            </div>
          </div>
        </div>
        <p className="policy-note">
          <Shield size={18} /> For laboratory research use only. Not for human or veterinary use. Documentation is
          provided for transparency and does not constitute medical advice, protocol guidance, or human-use instructions.
        </p>
      </div>
    </main>
  );
}

function DocumentationProcess() {
  return (
    <section className="section process-section">
      <div className="container">
        <SectionHead mark="Process" title="How every lot is documented" />
        <div className="process-grid">
          {processSteps.map(([title, body], index) => (
            <article className="process-card" key={title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CheckoutDrawer({
  open,
  onClose,
  selected,
  bulkQty
}: {
  open: boolean;
  onClose: () => void;
  selected: OfferSelection;
  bulkQty: number;
}) {
  const [a1, setA1] = useState(false);
  const [a2, setA2] = useState(false);
  const [a3, setA3] = useState(false);
  const [message, setMessage] = useState('');
  const { createSession } = useTagadaCheckoutSession();
  const summary = getOfferSummary(selected, bulkQty);
  const ready = a1 && a2 && a3;
  const configured = tagadaCheckoutConfigured(selected);

  async function handleContinue() {
    if (!ready) return;
    if (!configured) {
      setMessage('Tagada store and variant IDs are not configured yet.');
      return;
    }

    try {
      const session = await createSession(selected, bulkQty);
      setMessage(`Checkout session created: ${session.checkoutToken}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to create Tagada checkout session.');
    }
  }

  return (
    <div className={open ? 'drawer open' : 'drawer'} aria-hidden={!open}>
      <button className="drawer-backdrop" onClick={onClose} aria-label="Close checkout preview" />
      <aside className="drawer-panel">
        <div className="drawer-head">
          <div className="inline-kicker">
            <strong>Checkout preview</strong>
            <Badge tone="amber">Review mode</Badge>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="drawer-body">
          <div className="checkout-item">
            <div>
              <strong>{product.title}</strong>
              <span>
                {summary.quantity} vial{summary.quantity > 1 ? 's' : ''} - 10 mg sealed
              </span>
            </div>
            <b>{money(summary.total)}</b>
          </div>

          <label className="field">
            <span>Country eligibility</span>
            <select defaultValue="US">
              <option value="US">United States - 7-18 working days</option>
              <option value="GB">United Kingdom - 7-12 working days</option>
              <option value="DE">Germany - 8-15 working days</option>
              <option value="FR">France - 8-15 working days</option>
            </select>
          </label>

          <div className="attestations">
            <Kicker>Required attestations</Kicker>
            <Checkbox
              checked={a1}
              onChange={setA1}
              label="I confirm this purchase is for lawful laboratory research use only and not for human or veterinary use, diagnosis, treatment, prevention, resale, or human-use guidance."
            />
            <Checkbox
              checked={a2}
              onChange={setA2}
              label="I understand this is tracked international fulfillment, not domestic express delivery. Tracking may pause during export/import handoff."
            />
            <Checkbox
              checked={a3}
              onChange={setA3}
              label="I confirm I have the legal capacity to make this purchase in my destination country."
            />
          </div>
          {message && <p className="drawer-message">{message}</p>}
        </div>
        <div className="drawer-foot">
          <PrimaryButton full disabled={!ready} onClick={handleContinue}>
            {ready && configured ? 'Create Tagada checkout session' : ready ? 'Payment enabled once approved' : 'Complete attestations to continue'}
          </PrimaryButton>
          <p>No card is charged while processor approval is unfinished.</p>
        </div>
      </aside>
    </div>
  );
}

function Checkbox({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <label className="check-row">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function StickyCta({
  selected,
  bulkQty,
  onCheckout
}: {
  selected: OfferSelection;
  bulkQty: number;
  onCheckout: () => void;
}) {
  const summary = getOfferSummary(selected, bulkQty);

  return (
    <div className="sticky-cta">
      <div>
        <span>
          {summary.quantity} vial{summary.quantity > 1 ? 's' : ''} selected
        </span>
        <strong>{money(summary.total)}</strong>
      </div>
      <PrimaryButton onClick={onCheckout}>Checkout preview</PrimaryButton>
    </div>
  );
}

function TrustTicker() {
  const items = [
    'Lot-specific documentation',
    'Third-party COA',
    'Tracked global delivery',
    'Research-use checkout attestation',
    'Published shipping windows',
    'Support threshold policy'
  ];
  return (
    <div className="ticker">
      <div>
        {[...items, ...items].map((item, index) => (
          <span key={`${item}-${index}`}>
            <i /> {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function FounderNote() {
  return (
    <section className="founder-note">
      <div className="container narrow">
        <Kicker>Why we built this store</Kicker>
        <p>
          Research buyers should not have to guess about lot documentation, shipping windows, or support rules.
          We built Aminoscale around documented supply, not hype - and we do not provide medical advice,
          protocol guidance, or human-use instructions.
        </p>
      </div>
    </section>
  );
}

function Footer({ navigate }: { navigate: (route: NavRoute) => void }) {
  const businessDetails = [
    ['Operator', 'Kiln Holdings LLC'],
    ['Address', '30 N Gould St, Sheridan, WY 82801, United States'],
    ['Phone', '+1 (727) 660-2555']
  ];

  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div>
          <button className="footer-brand" onClick={() => navigate('home')}>
            <img src={mark} alt="" width="22" height="22" />
            <span>Aminoscale</span>
          </button>
          <p>Documented research supply, built with instrument-grade clarity. For laboratory research use only.</p>
          <div className="footer-legal" aria-label="Business information">
            {businessDetails.map(([label, value]) => (
              <div key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </div>
        <FooterColumn title="Product" links={['RT-10 Research Compound', 'Catalog', 'COA - RT-10']} navigate={navigate} />
        <FooterColumn title="Policies" links={['Shipping', 'Returns and reship', 'Research-use policy']} navigate={navigate} />
        <FooterColumn title="Support" links={['Contact', 'Tracking help', 'Documentation vault']} navigate={navigate} />
      </div>
      <div className="container footer-bottom">
        <span>(c) 2026 Aminoscale. For laboratory research use only. Not for human or veterinary use.</span>
        <span>RUO - no medical advice - tracked international</span>
      </div>
    </footer>
  );
}

function FooterColumn({
  title,
  links,
  navigate
}: {
  title: string;
  links: string[];
  navigate: (route: NavRoute) => void;
}) {
  return (
    <div className="footer-col">
      <strong>{title}</strong>
      {links.map((link) => (
        <button
          key={link}
          onClick={() => {
            if (link.includes('Catalog')) navigate('catalog');
            else if (link.includes('COA') || link.includes('Documentation')) navigate('coa');
            else navigate('pdp');
          }}
        >
          {link}
        </button>
      ))}
    </div>
  );
}

function ProofBar() {
  return (
    <div className="proof-bar">
      {product.proof.map((item, index) => {
        const Icon = iconMap[index] ?? Check;
        return (
          <span key={item}>
            <Icon size={15} /> {item}
          </span>
        );
      })}
    </div>
  );
}

function MiniLadder() {
  return (
    <div className="mini-ladder">
      {([1, 2, 3] as const).map((qty) => {
        const offer = AMINO_OFFERS[qty];
        return (
          <span className={qty === 2 ? 'featured' : ''} key={offer.label}>
            <small>{offer.label}</small>
            <strong>{money(offer.total)}</strong>
            <em>{offer.ribbon ?? offer.note}</em>
          </span>
        );
      })}
    </div>
  );
}

function SpecTable({ rows }: { rows: string[][] }) {
  return (
    <div className="spec-table">
      {rows.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

function Accordion({ items }: { items: string[][] }) {
  const [open, setOpen] = useState(0);

  return (
    <div className="accordion">
      {items.map(([title, body], index) => (
        <div className="accordion-item" key={title}>
          <button onClick={() => setOpen(open === index ? -1 : index)}>
            <span>{title}</span>
            <ChevronDown className={open === index ? 'rotate' : ''} size={16} />
          </button>
          {open === index && <p>{body}</p>}
        </div>
      ))}
    </div>
  );
}

function ComparisonCard({
  title,
  items,
  negative = false
}: {
  title: string;
  items: string[];
  negative?: boolean;
}) {
  return (
    <article className={negative ? 'compare-card muted' : 'compare-card positive'}>
      <h3>
        {negative ? <HelpCircle size={18} /> : <img src={mark} alt="" width="18" height="18" />}
        {title}
      </h3>
      {items.map((item) => (
        <p key={item}>
          {negative ? <X size={16} /> : <Check size={16} />} {item}
        </p>
      ))}
    </article>
  );
}

function SectionHead({ mark: eyebrow, title, sub }: { mark: string; title: string; sub?: string }) {
  return (
    <div className="section-head">
      <Kicker>{eyebrow}</Kicker>
      <h2>{title}</h2>
      {sub && <p>{sub}</p>}
    </div>
  );
}

function Kicker({ children }: { children: React.ReactNode }) {
  return <span className="kicker">{children}</span>;
}

function Badge({ children, tone }: { children: React.ReactNode; tone: 'green' | 'blue' | 'amber' }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

function PrimaryButton({
  children,
  onClick,
  full = false,
  disabled = false
}: {
  children: React.ReactNode;
  onClick?: () => void;
  full?: boolean;
  disabled?: boolean;
}) {
  return (
    <button className={full ? 'btn primary full' : 'btn primary'} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button className="btn secondary" onClick={onClick}>
      {children}
    </button>
  );
}

function SecondaryDarkButton({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button className="btn secondary-dark" onClick={onClick}>
      {children}
    </button>
  );
}

export default App;
